import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, radius } from '@/shared/theme/tokens';
import { Sparkline } from '../../ui/Sparkline';
import { LIVE_STATUS_META, statusBg, CATEGORY_LABEL } from './consensusMeta';

/**
 * One live alert row in the rail. Color tracks the *current* standing of the
 * (possibly jittered) value — so a recovered metric turns green. Flashes when
 * the simulator pulses it.
 */
export function AlertTickerItem({ alert, flashing, animate, onSelect }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const stale = !!alert.isStale;
  // Stale data must never be styled as a live in-/out-of-band reading, and must
  // never flash. Grey it out and let the "stale" tag carry the meaning.
  const meta = stale
    ? { color: c.textSubtle, border: c.border, bgLight: c.bgSubtle, bgDark: 'rgba(255,255,255,0.04)' }
    : (LIVE_STATUS_META[alert.status] ?? LIVE_STATUS_META.ok);
  const isFlashing = flashing && !stale;
  const clickable = typeof onSelect === 'function';

  return (
    <div
      onClick={clickable ? () => onSelect(alert) : undefined}
      onKeyDown={clickable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(alert); }
      } : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      title={clickable ? `Open ${alert.parameterName} alert` : undefined}
      style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 12px',
      borderRadius: radius.lg,
      cursor: clickable ? 'pointer' : 'default',
      opacity: stale ? 0.72 : 1,
      background: isFlashing ? meta.color : statusBg(meta, isDark),
      border: `1px solid ${meta.border}`,
      borderLeft: `4px solid ${meta.color}`,
      transition: animate ? 'background 0.25s ease' : 'none',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold,
            color: isFlashing ? '#fff' : c.textPrimary,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {alert.parameterName}
          </span>
          {stale && (
            <span style={{
              fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: c.textSubtle, flexShrink: 0,
              border: `1px solid ${c.border}`, borderRadius: radius.xs, padding: '1px 5px',
              textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide,
            }}>
              Stale
            </span>
          )}
        </div>
        <div style={{
          fontSize: typography.fontSize.xs,
          color: isFlashing ? 'rgba(255,255,255,0.85)' : c.textSubtle,
          textTransform: 'uppercase', letterSpacing: typography.letterSpacing.wide, marginTop: 1,
        }}>
          {CATEGORY_LABEL[alert.category] ?? alert.category}
        </div>
      </div>

      <div style={{ width: 64, flexShrink: 0, opacity: isFlashing ? 0.85 : 1 }}>
        <Sparkline data={alert.buffer} color={isFlashing ? '#fff' : meta.color} height={26} animate={false} />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, flexShrink: 0, minWidth: 64, justifyContent: 'flex-end' }}>
        <span style={{
          fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold,
          color: isFlashing ? '#fff' : meta.color,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {stale ? '—' : alert.liveValue}
        </span>
        {alert.unit && !stale && (
          <span style={{ fontSize: typography.fontSize.xs, color: isFlashing ? 'rgba(255,255,255,0.85)' : c.textSubtle }}>
            {alert.unit}
          </span>
        )}
      </div>
    </div>
  );
}
